import ProfileAvatar from '../common/ProfileAvatar';
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import FormattedCurrency from '../common/FormattedCurrency';
import { ChildQuote, QuoteOwner } from '../../types/quote';
import { JobQuote } from '../../types/job';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import { computeQuoteTotalAmount } from '../../utils/jobUtils';

interface JobFreelancerCardProps {
  quote: JobQuote;
  onClick?: () => void;
  className?: string;
}

interface InternalJobFreelancerCardProps {
  quoteOwner: QuoteOwner;
  teamMembers: ChildQuote[];
  totalAmount: number;
  statusIndicator: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const JobFreelancerCardMobile = ({
  quoteOwner,
  teamMembers,
  totalAmount,
  statusIndicator,
  className,
  onClick,
}: InternalJobFreelancerCardProps) => {
  const { t } = useTranslation(['quotes', 'common']);

  if (!quoteOwner) return null;

  return (
    <div className={twMerge('md:hidden w-full bg-white', className)}>
      <div
        className="flex items-center text-secondary hover:cursor-pointer"
        onClick={onClick}
        role="button"
      >
        <div className="flex flex-row gap-8">
          <ProfileAvatar
            profile_id={quoteOwner.id}
            first_name={quoteOwner.first_name}
            avatar_url={quoteOwner.avatar_url}
            className="flex-shrink-0"
          />

          <div className="flex flex-col">
            <span data-testid="freelancer-name" className="font-bold leading-tight">
              {quoteOwner.first_name} {quoteOwner.last_name}
            </span>
            <span>
              <span className="text-sm">
                {quoteOwner.services &&
                  getFreelancerSubtitle(
                    {
                      service_category_slugs: quoteOwner.services.map(service => service.slug),
                    },
                    t
                  )}
              </span>
            </span>
          </div>
        </div>
      </div>

      {teamMembers.length > 0 && (
        <div className="mt-6">
          <div className="text-sm text-gray-500 mb-2">{t('teamMembers', { ns: 'quotes' })}</div>
          <div className="flex gap-4">
            {teamMembers.map((childQuote, idx) => (
              <ProfileAvatar
                key={idx}
                profile_id={childQuote.quote_owner.id}
                first_name={childQuote.quote_owner.first_name}
                avatar_url={childQuote.quote_owner.avatar_url}
                className="w-12 h-12 flex-shrink-0"
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-between items-center">
        {(totalAmount ?? 0) > 0 && !(totalAmount === null || totalAmount === undefined) && (
          <div className="flex flex-col">
            <p className="text-sm leading-tight text-secondary">
              {t('createQuoteModal.offerLabel')}
            </p>
            {totalAmount === 0 && (
              <p className="text-base font-bold leading-tight text-secondary block">
                {t('childQuoteOfferPending')}
              </p>
            )}
            <p className="font-bold mt-2 text-lg text-secondary">
              {(totalAmount ?? 0) > 0 && <FormattedCurrency value={totalAmount!} />}
            </p>
          </div>
        )}

        {statusIndicator}

        {onClick && (
          <button
            type="button"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-primary  text-secondary hover:bg-gray-100"
            aria-label={t('createQuoteModal.showAllianceButtonLabel')}
            onClick={onClick}
          >
            <Eye className="h-5 w-5 text-secondary" />
          </button>
        )}
      </div>
    </div>
  );
};

const JobFreelancerCardDesktop = ({
  quoteOwner,
  teamMembers,
  totalAmount,
  statusIndicator,
  className,
  onClick,
}: InternalJobFreelancerCardProps) => {
  const { t } = useTranslation(['quotes', 'common']);
  return (
    <div
      role="button"
      onClick={onClick}
      className={twMerge(
        `hidden md:grid grid-cols-[3rem_1fr] gap-4 py-3 md:grid-cols-[50px_5fr_1fr] md:items-center`,
        className
      )}
    >
      <ProfileAvatar
        profile_id={quoteOwner.id}
        first_name={quoteOwner.first_name}
        avatar_url={quoteOwner.avatar_url}
        className="flex-shrink-0"
      />
      <div className="flex flex-row justify-between w-full">
        <div className="flex flex-col gap-4 md:flex-row md:gap-16">
          <div className="text-secondary hover:cursor-pointer" onClick={onClick} role="button">
            <div className="font-bold leading-tight">
              <span data-testid="freelancer-name">
                {quoteOwner.first_name} {quoteOwner.last_name}
              </span>
            </div>
            <span className="text-sm">
              {quoteOwner.services &&
                getFreelancerSubtitle(
                  {
                    service_category_slugs: quoteOwner.services.map(service => service.slug),
                  },
                  t
                )}
            </span>
          </div>

          {teamMembers.length > 0 && (
            <>
              <div className="flex flex-col gap-2">
                <p className="text-sm text-gray-500">{t('teamMembers', { ns: 'quotes' })}</p>
                <div className="flex gap-1">
                  {teamMembers.map((childQuote, idx) => (
                    <ProfileAvatar
                      key={idx}
                      profile_id={childQuote.quote_owner.id}
                      first_name={childQuote.quote_owner.first_name}
                      avatar_url={childQuote.quote_owner.avatar_url}
                      className="h-5 w-5 flex-shrink-0 text-[9px] leading-none"
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {(totalAmount ?? 0) > 0 && !(totalAmount === null || totalAmount === undefined) && (
            <div className="flex flex-col gap-2">
              <span className="text-sm leading-tight text-primary">
                {t('createQuoteModal.offerLabel')}
              </span>
              {totalAmount === 0 && (
                <span className="text-base font-bold leading-tight text-secondary block">
                  {t('childQuoteOfferPending')}
                </span>
              )}
              <span className="font-bold text-lg text-secondary">
                {(totalAmount ?? 0) > 0 && <FormattedCurrency value={totalAmount!} />}
              </span>
            </div>
          )}

          {statusIndicator && <div className="flex flex-col justify-center">{statusIndicator}</div>}
        </div>
      </div>

      <div className="hidden md:flex md:justify-end md:gap-6">
        {onClick && (
          <button
            type="button"
            className="hidden items-center justify-center h-8 w-8 rounded-full border border-[#A3B2C2] bg-white hover:bg-gray-100 flex-shrink-0 min-w-fit min-h-fit ml-2 md:flex md:justify-self-end"
            aria-label={t('createQuoteModal.showAllianceButtonLabel')}
            onClick={onClick}
          >
            <Eye className="h-5 w-5 text-secondary" />
          </button>
        )}
      </div>
    </div>
  );
};

const JobFreelancerCard: React.FC<JobFreelancerCardProps> = ({ quote, className, onClick }) => {
  const { t } = useTranslation(['quotes', 'common']);

  const quoteOwner = quote.quote_owner;
  const teamMembers = quote.alliances
    .filter(alliance => alliance.child_quote)
    .map(alliance => alliance.child_quote!);

  if (!quoteOwner) return null;

  const totalAmount = computeQuoteTotalAmount(quote);

  const statusIndicator =
    quote.status === 'accepted' ? (
      <span className="px-4 py-1 bg-green-600 text-sm text-white">
        {t('quote_statuses.accepted', { ns: 'common' })}
      </span>
    ) : quote.status === 'rejected' ? (
      <span className="px-4 py-1 bg-red-600 text-sm text-white">
        {t('quote_statuses.rejected', { ns: 'common' })}
      </span>
    ) : null;

  return (
    <>
      {/* Mobile version */}
      <JobFreelancerCardMobile
        totalAmount={totalAmount}
        quoteOwner={quoteOwner}
        teamMembers={teamMembers}
        statusIndicator={statusIndicator}
        className={className}
        onClick={onClick}
      />

      {/* Desktop version */}
      <JobFreelancerCardDesktop
        totalAmount={totalAmount}
        quoteOwner={quoteOwner}
        teamMembers={teamMembers}
        statusIndicator={statusIndicator}
        className={className}
        onClick={onClick}
      />
    </>
  );
};

export default JobFreelancerCard;
